import { readFile } from "./dwclient/dwstorage";
class PreviewCache {
    cache = new Map();
    maxCacheSize = 200 * 1024 * 1024; // 200MB max cache
    currentCacheSize = 0;
    maxAge = 30 * 60 * 1000; // 30 minutes
    // Get cached file or fetch new one
    async getFile(provider, props, path, size, onProgress) {
        const cacheKey = this.getCacheKey(path, provider);
        // Check if we have a valid cached entry
        const cached = this.cache.get(cacheKey);
        if (cached && this.isValidEntry(cached)) {
            // Move to end (most recently used)
            this.cache.delete(cacheKey);
            this.cache.set(cacheKey, cached);
            return cached.file;
        }
        // Cancel any ongoing fetch for the same file
        if (cached?.abortController) {
            cached.abortController.abort();
        }
        // Create abort controller for this fetch
        const abortController = new AbortController();
        try {
            // Fetch the file
            const file = await readFile(provider, props, path, size, onProgress);
            // Add to cache with actual file size
            this.addToCache(cacheKey, file, file.size || size || 0, abortController);
            return file;
        }
        catch (error) {
            // Remove failed entry
            this.cache.delete(cacheKey);
            throw error;
        }
    }
    // Add file to cache with LRU eviction
    addToCache(key, file, size, abortController) {
        // Use actual file size for accurate accounting
        const actualSize = file.size || size || 0;
        // Evict old entries if needed
        while (this.currentCacheSize + actualSize > this.maxCacheSize && this.cache.size > 0) {
            const firstKey = this.cache.keys().next().value;
            const firstEntry = this.cache.get(firstKey);
            if (firstEntry) {
                this.currentCacheSize -= firstEntry.size;
                firstEntry.abortController?.abort();
            }
            this.cache.delete(firstKey);
        }
        // Add new entry
        const entry = {
            file,
            timestamp: Date.now(),
            size: actualSize,
            abortController
        };
        this.cache.set(key, entry);
        this.currentCacheSize += actualSize;
    }
    // Check if cache entry is still valid
    isValidEntry(entry) {
        return Date.now() - entry.timestamp < this.maxAge;
    }
    // Generate cache key from path and provider context
    getCacheKey(path, provider) {
        // Include provider address to prevent cross-context cache pollution
        const providerKey = provider?.selectedAddress || 'default';
        return `${providerKey}:${path}`;
    }
    // Clear expired entries
    cleanExpired() {
        const now = Date.now();
        for (const [key, entry] of this.cache.entries()) {
            if (now - entry.timestamp >= this.maxAge) {
                this.currentCacheSize -= entry.size;
                entry.abortController?.abort();
                this.cache.delete(key);
            }
        }
    }
    // Clear all cache
    clear() {
        for (const entry of this.cache.values()) {
            entry.abortController?.abort();
        }
        this.cache.clear();
        this.currentCacheSize = 0;
    }
    // Get cache statistics
    getStats() {
        return {
            entries: this.cache.size,
            sizeBytes: this.currentCacheSize,
            sizeMB: (this.currentCacheSize / (1024 * 1024)).toFixed(2)
        };
    }
}
// Singleton instance
const previewCache = new PreviewCache();
// Export cached readFile function
export const readFileCached = async (provider, props, path, size, onProgress) => {
    return previewCache.getFile(provider, props, path, size, onProgress);
};
// Clear cache when provider changes
export const clearCacheForProvider = () => previewCache.clear();
// Export cache management functions
export const clearPreviewCache = () => previewCache.clear();
export const cleanExpiredCacheEntries = () => previewCache.cleanExpired();
export const getPreviewCacheStats = () => previewCache.getStats();
// Auto-clean expired entries every 5 minutes
if (typeof window !== 'undefined') {
    setInterval(() => {
        previewCache.cleanExpired();
    }, 5 * 60 * 1000);
}
